import type React from "react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode, useEffect } from "react";

export interface PlayerTrack {
  id: string;
  title: string;
  artist?: string | null;
  artwork?: string | null;
  /** Direct audio url (mp3/preview). */
  audioUrl?: string | null;
  /** YouTube video id or full YouTube / YouTube Music url. */
  youtube?: string | null;
  /** Optional external link (Spotify / Apple Music). */
  link?: string | null;
  /** Spotify / Apple Music embed url for full-track playback. */
  embedUrl?: string | null;
}

interface MediaPlayerContextValue {
  track: PlayerTrack | null;
  isPlaying: boolean;
  isYouTube: boolean;
  isEmbed: boolean;
  progress: number;
  duration: number;
  minimized: boolean;
  queue: PlayerTrack[];
  index: number;
  hasNext: boolean;
  hasPrev: boolean;
  play: (track: PlayerTrack) => void;
  playQueue: (tracks: PlayerTrack[], startIndex?: number) => void;
  next: () => void;
  prev: () => void;
  toggle: () => void;
  seek: (seconds: number) => void;
  close: () => void;
  setMinimized: (v: boolean) => void;
  isCurrent: (id: string) => boolean;
}

// Keep one context instance across hot reloads so the provider and consumers always match.
const g = globalThis as unknown as { __mcgMediaPlayerCtx?: React.Context<MediaPlayerContextValue | null> };
const MediaPlayerContext = g.__mcgMediaPlayerCtx ?? (g.__mcgMediaPlayerCtx = createContext<MediaPlayerContextValue | null>(null));

const noop = () => {};
const FALLBACK: MediaPlayerContextValue = {
  track: null, isPlaying: false, isYouTube: false, isEmbed: false, progress: 0, duration: 0, minimized: false,
  queue: [], index: 0, hasNext: false, hasPrev: false,
  play: noop, playQueue: noop, next: noop, prev: noop, toggle: noop, seek: noop, close: noop, setMinimized: noop,
  isCurrent: () => false,
};

/** Extract a YouTube video id from an id or any YouTube / YouTube Music url. */
export function youTubeId(value?: string | null): string | null {
  if (!value) return null;
  if (/^[\w-]{11}$/.test(value)) return value;
  const m = value.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export const MediaPlayerProvider = ({ children }: { children: ReactNode }) => {
  const [track, setTrack] = useState<PlayerTrack | null>(null);
  const [queue, setQueue] = useState<PlayerTrack[]>([]);
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const queueRef = useRef<{ queue: PlayerTrack[]; index: number }>({ queue: [], index: 0 });

  const isEmbed = !!track?.embedUrl;
  const isYouTube = !!track && !isEmbed && !track.audioUrl && !!youTubeId(track.youtube);

  const stopAudio = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
  }, []);

  const start = useCallback((next: PlayerTrack) => {
    stopAudio();
    setTrack(next);
    setMinimized(false);
    setProgress(0);
    setDuration(0);

    if (!next.embedUrl && next.audioUrl) {
      const audio = new Audio(next.audioUrl);
      audioRef.current = audio;
      audio.ontimeupdate = () => setProgress(audio.currentTime);
      audio.onloadedmetadata = () => setDuration(audio.duration || 0);
      audio.onended = () => {
        const { queue: q, index: i } = queueRef.current;
        if (i + 1 < q.length) {
          queueRef.current = { queue: q, index: i + 1 };
          setIndex(i + 1);
          startRef.current?.(q[i + 1]);
        } else setIsPlaying(false);
      };
      void audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      setIsPlaying(true);
    }
  }, [stopAudio]);
  const startRef = useRef(start);
  startRef.current = start;

  const playQueue = useCallback((tracks: PlayerTrack[], startIndex = 0) => {
    if (!tracks.length) return;
    const i = Math.min(Math.max(startIndex, 0), tracks.length - 1);
    queueRef.current = { queue: tracks, index: i };
    setQueue(tracks);
    setIndex(i);
    start(tracks[i]);
  }, [start]);

  const play = useCallback((next: PlayerTrack) => {
    const { queue: q } = queueRef.current;
    const at = q.findIndex((t) => t.id === next.id);
    if (at >= 0) {
      queueRef.current = { queue: q, index: at };
      setIndex(at);
      start(next);
    } else playQueue([next], 0);
  }, [start, playQueue]);

  const step = useCallback((dir: 1 | -1) => {
    const { queue: q, index: i } = queueRef.current;
    if (dir === -1 && audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    const n = i + dir;
    if (n < 0 || n >= q.length) return;
    queueRef.current = { queue: q, index: n };
    setIndex(n);
    start(q[n]);
  }, [start]);

  const next = useCallback(() => step(1), [step]);
  const prev = useCallback(() => step(-1), [step]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      if (audio.paused) { void audio.play(); setIsPlaying(true); }
      else { audio.pause(); setIsPlaying(false); }
      return;
    }
    setIsPlaying((p) => !p);
  }, []);

  const seek = useCallback((seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      setProgress(seconds);
    }
  }, []);

  const close = useCallback(() => {
    stopAudio();
    setTrack(null);
    setQueue([]);
    queueRef.current = { queue: [], index: 0 };
    setIsPlaying(false);
  }, [stopAudio]);

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const value = useMemo<MediaPlayerContextValue>(() => ({
    track, isPlaying, isYouTube, isEmbed, progress, duration, minimized, queue, index,
    hasNext: index + 1 < queue.length,
    hasPrev: index > 0,
    play, playQueue, next, prev, toggle, seek, close, setMinimized,
    isCurrent: (id: string) => track?.id === id && isPlaying,
  }), [track, isPlaying, isYouTube, isEmbed, progress, duration, minimized, queue, index, play, playQueue, next, prev, toggle, seek, close]);

  return <MediaPlayerContext.Provider value={value}>{children}</MediaPlayerContext.Provider>;
};

export function useMediaPlayer() {
  const ctx = useContext(MediaPlayerContext);
  if (!ctx) {
    console.warn("useMediaPlayer used outside MediaPlayerProvider; playback disabled here.");
    return FALLBACK;
  }
  return ctx;
}
